import { ShieldCheck } from 'lucide-react';

import { pluralize } from '@/react/common/string-utils';

import { Widget } from '@@/Widget';
import { InlineLoader } from '@@/InlineLoader';
import { TooltipWithChildren } from '@@/Tip/TooltipWithChildren';
import { StackedStatusBar } from '@@/StackedStatusBar';

import { Policy, PolicyStatusBreakdown } from '../types';
import { PolicyTypeBadge } from '../components/PolicyTypeBadge';

/** Props for the PolicyHeader component */
interface Props {
  /** The policy data to display */
  policy?: Policy;
  /** Whether the policy is still loading */
  isLoading?: boolean;
}

/**
 * Header component for the policy detail view.
 * Displays policy name, type badge, and a status progress bar.
 */
export function PolicyHeader({ policy, isLoading }: Props) {
  if (isLoading) {
    return (
      <Widget>
        <div className="flex items-center gap-4 p-4">
          <InlineLoader>Loading policy...</InlineLoader>
        </div>
      </Widget>
    );
  }

  if (!policy) {
    return null;
  }

  return (
    <Widget>
      <div className="flex items-center gap-4 p-4" data-cy="policy-header">
        {/* Icon container */}
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-blue-3 th-dark:bg-blue-9">
          <ShieldCheck className="h-6 w-6 text-blue-9 th-dark:text-blue-3" />
        </div>

        {/* Policy info */}
        <div className="flex flex-1 flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="text-muted text-xs font-medium uppercase">
              Policy
            </span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-lg font-semibold">{policy.Name}</span>
            <PolicyTypeBadge
              policyType={policy.Type}
              data-cy="policy-type-badge"
            />
          </div>
        </div>

        {/* Status progress bar */}
        <PolicyStatusProgressBar statusBreakdown={policy.StatusBreakdown} />
      </div>
    </Widget>
  );
}

interface PolicyStatusProgressBarProps {
  statusBreakdown?: PolicyStatusBreakdown;
}

/**
 * Displays a colored progress bar showing policy rollout status.
 * Shows a tooltip with environment counts on hover.
 */
export function PolicyStatusProgressBar({
  statusBreakdown,
}: PolicyStatusProgressBarProps) {
  if (!statusBreakdown) {
    return null;
  }

  const total =
    statusBreakdown.Applied +
    statusBreakdown.Warning +
    statusBreakdown.Failed +
    statusBreakdown.InProgress +
    statusBreakdown.NotSupported;

  if (total === 0) {
    return <span className="text-muted text-sm">No environments targeted</span>;
  }

  const tooltipMessage = formatStatusTooltip(statusBreakdown, total);

  return (
    <TooltipWithChildren message={tooltipMessage}>
      <div
        className="flex flex-col items-end gap-1"
        data-cy="policy-status-bar"
      >
        <StackedStatusBar
          className="!w-48"
          steps={[
            {
              value: statusBreakdown.Applied,
              className: 'bg-success-7',
              label: 'Active',
            },
            {
              value: statusBreakdown.Warning,
              className: 'bg-warning-7',
              label: 'Warning',
            },
            {
              value: statusBreakdown.InProgress,
              className: 'bg-blue-7',
              label: 'In progress',
            },
            {
              value: statusBreakdown.Failed,
              className: 'bg-error-7',
              label: 'Failed',
            },
            {
              value: statusBreakdown.NotSupported,
              className: 'bg-gray-7',
              label: 'Not supported',
            },
          ]}
          total={total}
          aria-label="Policy status"
        />
      </div>
    </TooltipWithChildren>
  );
}

/**
 * Formats the tooltip message for the status progress bar.
 * @param breakdown - The policy status breakdown counts
 * @param total - Total number of environments
 * @returns Formatted tooltip string
 */
export function formatStatusTooltip(
  breakdown: PolicyStatusBreakdown,
  total: number
): string {
  const parts: Array<string> = [];

  if (breakdown.Applied > 0) {
    parts.push(`${breakdown.Applied} active`);
  }
  if (breakdown.InProgress > 0) {
    parts.push(`${breakdown.InProgress} in progress`);
  }
  if (breakdown.Failed > 0) {
    parts.push(`${breakdown.Failed} failed`);
  }
  if (breakdown.NotSupported > 0) {
    parts.push(`${breakdown.NotSupported} not supported`);
  }
  if (breakdown.Warning > 0) {
    parts.push(
      `${breakdown.Warning} with ${pluralize(breakdown.Warning, 'warning')}`
    );
  }

  const envText = `${total} ${pluralize(total, 'environment')}`;
  return parts.length > 0 ? `${envText}: ${parts.join(', ')}` : envText;
}
